import {App, Plugin} from 'vue';
import {Router} from 'vue-router';
import {init, setTag} from '@sentry/vue';
import isbot from 'isbot';

export const sentry: Plugin = {
  install(
    app: App,
    {router, version}: {router: Router; version: string}
  ): void {
    if (import.meta.env.VITE_SENTRY_DSN === undefined) {
      console.warn('No sentry dsn configured');
      return;
    }

    setTag('sw_version', 'v1');

    setTag('bot', isbot(navigator.userAgent) ? 'yes' : 'no');

    init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.PROD ? 'production' : 'development',
      release: version,
      ignoreErrors: [
        /Network Error/,
        /Failed to fetch/,
        /newestWorker is null/,
        /Importing a module script failed\./,
        /Unable to preload CSS for/,
        /An attempt was made to use an object that is not, or is no longer, usable/,
        /Failed to update a ServiceWorker for scope/,
        /Non-recoverable error\. Do not retry\./, // Facebook/Instagram in-app browser crypto failures
        /Launcher Failed/, // Safari/iOS WebKit internal PWA launcher errors
        /^TypeError: Internal error$/, // Safari/WebKit cache API bug (WebKit #261767)
        /The object does not support the operation or argument/, // iOS facebook webview error with zero user impact
        /feature named .+ was not found/, // WebKit/DuckDuckGo browser-internal errors (no stacktrace, no user impact)
      ],
      beforeSend: (event, hint) => {
        try {
          const exceptions = event.exception?.values || [];
          const firstException = exceptions[0];
          const firstExceptionStack = firstException?.stacktrace?.frames || [];
          const firstExceptionMessage = firstException?.value || '';

          /**
           * Exclude errors from unknown/third-party sources (check all frames).
           */
          const frameExclusions = [
            /<anonymous>/,
            /www\.paypal\.com\/sdk\/js/,
            /js\.stripe\.com\/v3/,
            /translate\.google\.com/,
            /translate_http/,
            /recaptcha\/releases/,
          ];
          if (
            firstExceptionStack.some(frame =>
              frameExclusions.some(m => frame.filename?.match(m))
            )
          ) {
            return null;
          }

          /**
           * Exclude Safari/WebKit internal errors (masked URLs).
           * These have no actionable application code in the stacktrace.
           */
          const allFramesAreMasked =
            firstExceptionStack.length > 0 &&
            firstExceptionStack.every(
              frame =>
                frame.filename?.includes('webkit-masked-url') ||
                frame.filename === '://hidden/'
            );
          if (allFramesAreMasked) {
            return null;
          }

          /**
           * Exclude known-noisy touch tracking errors in the PBR bundle.
           * These are observed on iOS Safari where touch lifecycle events can be
           * delivered out of order (e.g. touchmove/touchend without a matching touchstart),
           * causing undefined touch state access inside the PBR viewer bundle.
           *
           * Scope tightly to the bundle path + message substring to avoid hiding
           * real application TypeErrors.
           */
          const isPbrBundleFrame = firstExceptionStack.some(frame =>
            frame.filename?.includes('/bundles/bundle/pbr/')
          );
          if (isPbrBundleFrame && firstExceptionMessage.includes('updateUvTouches')) {
            return null;
          }

          return event;
        } catch (error) {
          /**
           * If exclusion filtering fails send error anyway.
           */
          return event;
        }
      },
    });
  },
};
